import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import useStyles from "../styles";
import {
  Discount,
  DiscountAmountType,
  DiscountHandoutType,
  DiscountWriteModel,
  toWriteModel
} from "../../../models/Discount";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { Market } from "../../../models/Market";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { thunkFetchCurrentMarket } from "../../../store/markets/actions";
import CloseIcon from "@material-ui/icons/Close";
import { randomNumber } from "../../../utils";
import {
  getDiscountType,
  isPeriodicDiscount,
  isScannableDiscount,
  isValidAmount,
  isValidCode,
  isValidDiscount,
  isValidEndDate,
  isValidMinimumAmount,
  isValidName,
  isValidStartDate
} from "../utils";
import { DiscountTypeSelector } from "./DiscountTypeSelector";
import { DiscountType } from "../types";
import { StepButtons } from "../../dashboard/components/OnBoarding/components/StepButtons";
import { Restore, Update } from "@material-ui/icons";
import { VolunteerActivism } from "@mui/icons-material";
import { useI18n } from "../../../hooks/useI18n";

interface DiscountEditorDialogProps {
  discount?: Discount;
  title?: string;
  isEditing: boolean;
  isLoading: boolean;
  onCancel: () => void;
  discountTypes: ReadonlyArray<DiscountType>;
  onConfirm: Function;
}

interface State {
  model: DiscountWriteModel;
}

export const DiscountEditorDialog: FunctionComponent<
  DiscountEditorDialogProps
> = ({
  discount,
  onCancel,
  onConfirm,
  discountTypes,
  title = "Discount",
  isLoading = false,
  isEditing = true
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(useTheme().breakpoints.down("xs"));
  const market =
    useSelector<AppState, Market>(s => s.currentMarket) || ({} as Market);
  const { t } = useI18n();

  useLoadIfEmpty(!market?.currency && !market?.id, thunkFetchCurrentMarket);

  const getInitState = (discount: Discount) => {
    return {
      model: discount ? toWriteModel(discount) : undefined
    } as State;
  };

  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState<State>(getInitState(discount as Discount));

  useEffect(() => {
    if (!state.model && !!discount && !isLoading) {
      const disc = getInitState(discount as Discount);
      setState(disc);
    }
  }, [discount]);

  const handleConfirm = (): boolean => {
    if (!isValidDiscount(state.model, discountTypes)) return false;
    onConfirm(state.model);
    return false;
  };

  const calcMinimumPurchaseAmount = (
    amount?: number,
    amountType?: DiscountAmountType
  ): number | undefined => {
    if (!amount || !amountType) {
      return state.model.minimumPurchaseAmount;
    }

    return amountType === "Amount" &&
      (state.model.minimumPurchaseAmount === undefined ||
        amount > state.model.minimumPurchaseAmount)
      ? amount
      : state.model.minimumPurchaseAmount;
  };

  const onDiscountTypeSelected = (type: number) => {
    let code: string;
    let active = state.model.active;

    if (state.model.type !== type) {
      active = undefined;
    }

    if (isPeriodicDiscount(type, discountTypes)) {
      const t = type || 3;
      // @ts-ignore
      code = `${getDiscountType(t, discountTypes).name}-${randomNumber(
        1,
        10000
      )}`;

      // All other discounts can be active by default but not periodic discounts
      active = active ?? false;
    } else if (isScannableDiscount(type, discountTypes)) {
      code = "";
    } else {
      code = "";
    }

    setState({
      ...state,
      model: {
        ...state.model,
        code: code.toUpperCase() || undefined,
        type: type,
        active: active
      }
    });
  };

  const setCode = (code: string) => {
    const upCode = code.toUpperCase();
    setState({
      ...state,
      model: {
        ...state.model,
        code: upCode
      }
    });
  };

  const requireMinPurchaseAmount = (): boolean => {
    return isPeriodicDiscount(state.model.type, discountTypes);
  };

  const getSubTextForAmount = () => {
    const discount = discountTypes.find(x => x.id === state.model.type);
    return discount?.description;
  };

  const getTitleForAmount = () => {
    const discount = discountTypes.find(x => x.id === state.model.type);
    return discount?.label;
  };

  const getHandoutText = () => {
    if (state.model.type === 2 || state.model.type === 3) {
      return "to use on a future purchase";
    }

    switch (state.model.handoutType) {
      case DiscountHandoutType.BeforePurchase:
        return "on the current purchase";
      case DiscountHandoutType.AfterPurchase:
        return "to use on a future purchase";
      case DiscountHandoutType.Manually:
        return "on a purchase to be handed out manually from back-office";
    }
  };

  const disableNext = () => {
    let disable = false;
    if (activeStep === 1) {
      if (state.model.type === 1 || state.model.type === 4) {
        disable = state.model.handoutType === undefined;
      }
      disable =
        disable ||
        !isValidAmount(state.model.amount, state.model.amountType) ||
        !isValidMinimumAmount(state.model, discountTypes);
    }

    if (activeStep === 2) {
      if (
        state.model.type === 0 ||
        state.model.type === 1 ||
        state.model.type === 4
      ) {
        disable =
          !isValidStartDate(state.model.start) ||
          !isValidEndDate(state.model.end);
      }

      if (state.model.type === 0) {
        disable = disable || !isValidCode(state.model.code);
      }

      disable = disable || !isValidName(state.model.name);
    }

    return disable;
  };

  return (
    <Dialog open={true} fullWidth={true} fullScreen={mobile} maxWidth={"md"}>
      {!state.model || !discountTypes.length ? (
        <Box
          sx={{
            minHeight: "6rem",
            width: "100%",
            display: "flex",
            flexGrow: 1,
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle
            disableTypography={true}
            style={{
              paddingBottom: 4,
              paddingTop: 8,
              borderBottom: `1px solid ${theme.palette.grey.A100}`
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Typography variant={"h6"}>{title}</Typography>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onCancel}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: "100%" }}>
              <Stepper activeStep={activeStep}>
                <Step key={1}>
                  <StepLabel>{t("common.messages.couponType")}</StepLabel>
                </Step>
                <Step key={2}>
                  <StepLabel>{t("common.messages.couponAmount")}</StepLabel>
                </Step>
                <Step key={3}>
                  <StepLabel>{t("common.messages.finish")}</StepLabel>
                </Step>
              </Stepper>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column"
              }}
            >
              {activeStep === 0 && (
                <>
                  <Typography variant={"h6"}>
                    {t("common.messages.couponType")}
                  </Typography>
                  <Typography
                    className={classes.helpTitle}
                    variant={"subtitle1"}
                  >
                    Select the type of coupon you want to create
                  </Typography>
                  <Box pt={1} pb={4}>
                    {discountTypes.length && (
                      <DiscountTypeSelector
                        discountTypes={discountTypes}
                        discount={state.model}
                        disabled={isEditing}
                        onSelect={(typeId: number) =>
                          onDiscountTypeSelected(typeId)
                        }
                      />
                    )}
                  </Box>
                </>
              )}

              {activeStep === 1 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      width: "80%"
                    }}
                  >
                    <Typography variant={"h6"}>
                      {getTitleForAmount()}
                    </Typography>
                    <Typography
                      className={classes.helpTitle}
                      variant={"subtitle1"}
                      style={{ textAlign: "center" }}
                    >
                      {getSubTextForAmount()}
                    </Typography>
                  </Box>
                  <Grid container style={{ padding: theme.spacing(5) }}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        required
                        className={classes.field}
                        label={t("common.formFields.description")}
                        type={"number"}
                        value={state.model.amount ?? ""}
                        onChange={e => {
                          let amount = Number(e.target.value);
                          return setState({
                            ...state,
                            model: {
                              ...state.model,
                              amount: Number(e.target.value),
                              minimumPurchaseAmount: calcMinimumPurchaseAmount(
                                amount,
                                state.model.amountType
                              )
                            }
                          });
                        }}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            max:
                              state.model.amountType === "Percentage"
                                ? 100
                                : Number.MAX_VALUE
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <FormControl
                                required
                                className={classes.field}
                                variant={"standard"}
                              >
                                <InputLabel id="amount-type-select-label">
                                  Type
                                </InputLabel>
                                <Select
                                  labelId="amount-type-select-label"
                                  value={state.model.amountType ?? ""}
                                  onChange={e => {
                                    let amountType = e.target
                                      .value as DiscountAmountType;
                                    return setState({
                                      ...state,
                                      model: {
                                        ...state.model,
                                        amountType: amountType
                                      }
                                    });
                                  }}
                                >
                                  <MenuItem
                                    key={"Percentage"}
                                    value={"Percentage"}
                                  >
                                    % off on total
                                  </MenuItem>
                                  <MenuItem key={"Amount"} value={"Amount"}>
                                    {market.currencySymbol} off on total
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={2}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      when
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        className={classes.field}
                        label={
                          state.model.type === 2 || state.model.type === 3
                            ? `${
                                state.model.type === 2 ? "monthly" : "weekly"
                              } purchase amount is at least`
                            : "purchase amount is at least"
                        }
                        type="number"
                        required={requireMinPurchaseAmount()}
                        helperText={
                          requireMinPurchaseAmount()
                            ? ""
                            : "Leave empty to apply to any purchase"
                        }
                        value={state.model.minimumPurchaseAmount}
                        InputProps={{
                          inputProps: { min: 1, max: Number.MAX_VALUE },
                          endAdornment: (
                            <InputAdornment position="end">
                              {market.currencySymbol}
                            </InputAdornment>
                          )
                        }}
                        onChange={e => {
                          setState({
                            ...state,
                            model: {
                              ...state.model,
                              minimumPurchaseAmount:
                                Number(e.target.value) > 0
                                  ? Number(e.target.value)
                                  : undefined
                            }
                          });
                        }}
                      />
                    </Grid>

                    {(state.model.type === 1 || state.model.type === 4) && (
                      <Grid item xs={12} md={8}>
                        <Typography
                          variant={"h6"}
                          style={{ marginTop: theme.spacing(3) }}
                        >
                          Apply the discount:
                        </Typography>
                        <List>
                          <ListItem
                            onClick={() =>
                              setState({
                                ...state,
                                model: {
                                  ...state.model,
                                  handoutType:
                                    DiscountHandoutType.BeforePurchase
                                }
                              })
                            }
                            style={{
                              cursor: "pointer",
                              paddingLeft: 0,
                              color:
                                state.model.handoutType ===
                                DiscountHandoutType.BeforePurchase
                                  ? theme.palette.primary.dark
                                  : theme.palette.text.primary
                            }}
                          >
                            <ListItemIcon>
                              <Restore
                                fontSize={"large"}
                                style={{
                                  color:
                                    state.model.handoutType ===
                                    DiscountHandoutType.BeforePurchase
                                      ? theme.palette.primary.dark
                                      : theme.palette.text.secondary
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("common.messages.beforePurchase")}
                              secondary={
                                "The discount is applied on the current purchase"
                              }
                            />
                          </ListItem>
                          <ListItem
                            onClick={() =>
                              setState({
                                ...state,
                                model: {
                                  ...state.model,
                                  handoutType: DiscountHandoutType.AfterPurchase
                                }
                              })
                            }
                            style={{
                              cursor: "pointer",
                              paddingLeft: 0,
                              color:
                                state.model.handoutType ===
                                DiscountHandoutType.AfterPurchase
                                  ? theme.palette.primary.dark
                                  : theme.palette.text.primary
                            }}
                          >
                            <ListItemIcon>
                              <Update
                                fontSize={"large"}
                                style={{
                                  color:
                                    state.model.handoutType ===
                                    DiscountHandoutType.AfterPurchase
                                      ? theme.palette.primary.dark
                                      : theme.palette.text.secondary
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("common.messages.afterPurchase")}
                              secondary={
                                "The discount is applied after the purchase has been made and can be used for a future purchase"
                              }
                            />
                          </ListItem>
                          <ListItem
                            onClick={() =>
                              setState({
                                ...state,
                                model: {
                                  ...state.model,
                                  handoutType: DiscountHandoutType.Manually
                                }
                              })
                            }
                            style={{
                              cursor: "pointer",
                              paddingLeft: 0,
                              color:
                                state.model.handoutType ===
                                DiscountHandoutType.Manually
                                  ? theme.palette.primary.dark
                                  : theme.palette.text.primary
                            }}
                          >
                            <ListItemIcon>
                              <VolunteerActivism
                                fontSize={"large"}
                                style={{
                                  color:
                                    state.model.handoutType ===
                                    DiscountHandoutType.Manually
                                      ? theme.palette.primary.dark
                                      : theme.palette.text.secondary
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={"Manually"}
                              secondary={
                                "The discount can be handed out to a user manually from the back-office"
                              }
                            />
                          </ListItem>
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      width: "80%"
                    }}
                  >
                    <Typography variant={"h6"}>
                      {getTitleForAmount()}
                    </Typography>
                    <Typography
                      className={classes.helpTitle}
                      variant={"subtitle1"}
                      style={{ textAlign: "center" }}
                    >
                      Give the customer{" "}
                      <strong>
                        {state.model.amount}{" "}
                        {state.model.amountType === "Percentage"
                          ? "%"
                          : market.currencySymbol}
                      </strong>{" "}
                      off {getHandoutText()}
                      {state.model.minimumPurchaseAmount !== undefined && (
                        <>
                          {" "}
                          when{" "}
                          {state.model.type === 2 || state.model.type === 3
                            ? `${
                                state.model.type === 2 ? "monthly" : "weekly"
                              } purchase amount is at least`
                            : "purchase amount is at least"}
                          <strong>{` ${state.model.minimumPurchaseAmount} ${market.currencySymbol}`}</strong>
                        </>
                      )}
                    </Typography>
                  </Box>
                  <Grid
                    container
                    style={{ padding: theme.spacing(5), paddingTop: 0 }}
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                      className={classes.gridHorizontalPad}
                    >
                      <TextField
                        required
                        className={classes.field}
                        label={t("common.formFields.couponName")}
                        type="text"
                        helperText={"Visible in back-office only"}
                        value={state.model.name ?? ""}
                        onChange={e =>
                          setState({
                            ...state,
                            model: {
                              ...state.model,
                              name: e.target.value as string
                            }
                          })
                        }
                      />
                    </Grid>
                    {(state.model.type === 1 || state.model.type === 4) && (
                      <Grid item xs={12} md={6}>
                        &nbsp;
                      </Grid>
                    )}

                    {state.model.type === 0 && (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        className={classes.gridHorizontalPad}
                      >
                        <TextField
                          required
                          disabled={isEditing}
                          className={classes.field}
                          label={t("common.formFields.name")}
                          type="text"
                          value={state.model.code ?? ""}
                          onChange={e => setCode(e.target.value as string)}
                          helperText={
                            <>
                              Only supports: <b>numbers</b>, <b>letters</b>,{" "}
                              <b>-</b> and <b>_</b>
                            </>
                          }
                        />
                      </Grid>
                    )}
                    {!isPeriodicDiscount(
                      state.model.type as number,
                      discountTypes
                    ) ? (
                      <>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          className={classes.gridHorizontalPad}
                        >
                          <KeyboardDatePicker
                            required
                            disableToolbar
                            className={classes.field}
                            variant="inline"
                            format="YYYY-MM-DD"
                            margin="normal"
                            id="valid-from-date"
                            label={t("common.formFields.validFrom")}
                            value={state.model.start ? state.model.start : null}
                            onChange={(date: MaterialUiPickersDate) => {
                              if (date) {
                                return setState({
                                  ...state,
                                  model: {
                                    ...state.model,
                                    start: new Date(date.format("YYYY-MM-DD"))
                                  }
                                });
                              }
                            }}
                            KeyboardButtonProps={{
                              "aria-label": "change valid from"
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          className={classes.gridHorizontalPad}
                        >
                          <KeyboardDatePicker
                            required
                            disableToolbar
                            className={classes.field}
                            variant="inline"
                            format="YYYY-MM-DD"
                            margin="normal"
                            id="valid-to-date"
                            label={t("common.formFields.validTo")}
                            value={state.model.end ? state.model.end : null}
                            onChange={(date: MaterialUiPickersDate) => {
                              if (date) {
                                return setState({
                                  ...state,
                                  model: {
                                    ...state.model,
                                    end: new Date(date.format("YYYY-MM-DD"))
                                  }
                                });
                              }
                            }}
                            KeyboardButtonProps={{
                              "aria-label": "change valid from"
                            }}
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Box pt={1}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                value={state.model.active}
                                checked={state.model.active}
                                color={"primary"}
                                onChange={(_, checked) => {
                                  setState({
                                    ...state,
                                    model: { ...state.model, active: checked }
                                  });
                                }}
                              />
                            }
                            label={"Active"}
                            labelPlacement={"end"}
                          />
                          <FormHelperText>
                            Activating this discount will disable other active
                            periodic discounts of the same type (<b>Week</b>/
                            <b>Month</b>) for this tenant.
                          </FormHelperText>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Box>
            <StepButtons
              nextDisabled={disableNext()}
              backDisabled={activeStep === 0}
              onClickNext={() => {
                activeStep === 2
                  ? handleConfirm()
                  : setActiveStep(activeStep + 1);
              }}
              onClickBack={() => setActiveStep(activeStep - 1)}
              lastStep={activeStep === 2}
            />
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
